import { useQuery } from '@tanstack/react-query';
import { useSidebar } from '@portal/platform/configuration';
import { useSession } from '@portal/platform/session';
import type { NavBadges } from '@portal/ui';
import { complianceReportQueryOptions } from '../features/compliance-monitor/queries';
import { leadsListQueryOptions } from '../features/leads/queries';

/** The menu's work lists, pinned until the user changes it (navigation design A). */
export const DEFAULT_PINS = ['leads', 'service-agreement-compliance-monitor'] as const;

/** Counts are a nudge, not a live feed; the lists' own writes refresh them sooner. */
const STALE_MS = 2 * 60_000;

/**
 * How much is waiting in the work lists, for the menu's badges: open leads, and agreements over
 * their km allowance. One-row reads of the lists' own queries (`total` only), keyed under the
 * lists' keys so a change made on the page invalidates the badge with it. Only asked for when the
 * entry is in the user's menu — the layout has already checked feature and permission.
 */
export function useNavBadges(): NavBadges {
  const { platform } = useSession();
  const client = platform.client;
  const { sections } = useSidebar({ keepUnmatched: true });
  const visible = new Set(sections.flatMap(section => section.items.map(item => item.id)));

  const leads = useQuery({
    ...leadsListQueryOptions(client, {
      view: 'open',
      flow: null,
      source: null,
      onlyUnhandled: false,
      search: '',
      page: 1,
      limit: 1,
    }),
    enabled: visible.has('leads'),
    staleTime: STALE_MS,
    retry: false,
    select: page => page.total,
  });
  const overKm = useQuery({
    ...complianceReportQueryOptions(client, {
      tab: 'WATCH',
      search: '',
      onlyOver: true,
      page: 1,
      limit: 1,
    }),
    enabled: visible.has('service-agreement-compliance-monitor'),
    staleTime: STALE_MS,
    retry: false,
    select: page => page.total,
  });

  return {
    leads: leads.data,
    'service-agreement-compliance-monitor': overKm.data,
  };
}
